import React, { useEffect, useState, useRef } from 'react';
import { useDataProvider, useTranslate } from 'react-admin';
import {
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { formatDistanceToNow } from 'date-fns';
import moment from 'moment-timezone';
import 'moment/locale/ar'; // Import Arabic locale
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const NotificationsDropdown = ({ anchorEl, onClose }) => {
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  const ITEMS_PER_PAGE = 10; // Load 20 notifications per scroll

  useEffect(() => {
    if (!anchorEl) return;

    setNotifications([]);
    setPage(1);
    setHasMore(true);
    fetchNotifications(1);

    // Mark all notifications as read when opened
    markAllAsRead();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  const fetchNotifications = async (pageToFetch) => {
    setLoading(true);
    try {
      const response = await dataProvider.getList('notifications', {
        pagination: { page: pageToFetch, perPage: ITEMS_PER_PAGE },
        sort: { field: 'created_at', order: 'DESC' },
        filter: {},
      });
      setNotifications((prev) => [...prev, ...response.data]);
      setHasMore(response.total > pageToFetch * ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await dataProvider.getList('notifications', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { is_web_read: false },
      });
      const ids = response.data.map((notification) => notification.id);
      if (ids.length > 0) {
        await dataProvider.updateMany('notifications', {
          ids,
          data: { is_web_read: true },
        });
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
      const nextPage = page + 1;
      fetchNotifications(nextPage);
      setPage(nextPage);
    }
  };

   // Updated formatDate function
   const formatDate = (dateString) => {
    const timeZone = 'Asia/Riyadh'; // Saudi Arabia time zone
  
    // Remove fractional seconds
    const adjustedDateString = dateString.replace(/\.\d+$/, '');
  
    // Parse date in UTC and convert to Saudi Arabia time zone
    const notificationTime = moment.utc(adjustedDateString, 'YYYY-MM-DD HH:mm:ss').tz(timeZone);
  
    const currentTimeInSaudi = moment().tz(timeZone);
  
    return notificationTime.locale('ar').from(currentTimeInSaudi);
  };
  

  
  

  const handleNotificationClick = (notification) => {
    // Assuming the notification contains 'resource' and 'resource_id'
    const resource = notification.resource || 'training_courses';
    const resourceId = notification.course_id || 429;

    // Close the dropdown
    onClose();

    // Navigate to the desired route
    navigate(`/${resource}/${resourceId}`);
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ style: { width: 300, maxHeight: 400 } }} // Adjust width if needed
    >
         <Typography variant="h6" sx={{ padding: 2 }}>
         الإشعارات
      </Typography>
      <Divider />

      <div
    ref={listRef}
    style={{ maxHeight: 350, overflow: 'auto', padding: 0 }}
    onScroll={handleScroll}
    dir="rtl" // Set RTL direction
  >


<List style={{ padding: 0 }}>

        {notifications.map((notification) => (
          <React.Fragment key={notification.id}>
            <StyledListItem
              button
              onClick={() => handleNotificationClick(notification)}
            >
              <ListItemAvatar>
                <Avatar sx={{ width: 32, height: 32 }}>
                  <NotificationsIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    noWrap // Keep text on a single line
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      noWrap // Keep text on a single line
                    >
                      {notification.body}
                    </Typography>
                    <Typography
                      component="span"
                      variant="caption"
                      color="textSecondary"
                    >
                      {formatDate(notification.created_at)}
                    </Typography>
                  </>
                }
                sx={{ margin: 0 }}
              />
            </StyledListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
        {loading && (
          <ListItem dir="rtl">
            <CircularProgress size={24} />
          </ListItem>
        )}
        {!loading && notifications.length === 0 && (
          <ListItem dir="rtl">
            <ListItemText primary={"لا يوجد إشعارات"} />
          </ListItem>
        )}
        {!hasMore && notifications.length > 0 && (
          <ListItem dir="rtl">
            <ListItemText primary={"لا مزيد من الإشعارات"} />
          </ListItem>
        )}
      </List>
      </div>
    </Popover>
  );
};

// Styled component to handle RTL layout
const StyledListItem = styled(ListItem)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  textAlign: 'right', // Align text to the right
  '& .MuiListItemText-root': {
    textAlign: 'right',
  },
}));

export default NotificationsDropdown;
